import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from '@/contexts/LanguageContext';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isChunkLoadError: boolean;
}

// Helper to check if error is a chunk load error
const isChunkLoadError = (error: Error | null): boolean => {
  if (!error) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('chunk load error') ||
    message.includes('loading css chunk') ||
    message.includes('importing a module script failed') ||
    message.includes('failed to fetch')
  );
};

// Error reporting service
const reportErrorToBackend = async (
  error: Error, 
  errorInfo: ErrorInfo,
  isChunkError: boolean = false
): Promise<void> => {
  try {
    const { error: reportError } = await supabase.functions.invoke('report-error', {
      body: {
        error_message: error.message,
        error_stack: error.stack,
        component_stack: errorInfo.componentStack,
        url: window.location.href,
        user_agent: navigator.userAgent,
        metadata: {
          timestamp: new Date().toISOString(),
          pathname: window.location.pathname,
          referrer: document.referrer,
          errorType: isChunkError ? 'chunk_load_error' : 'runtime_error',
        },
      },
    });

    if (reportError) {
      console.error('Failed to report error to backend:', reportError);
    } else {
      console.log('Error reported to backend successfully');
    }
  } catch (e) {
    console.error('Failed to send error report:', e);
  }
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
      isChunkLoadError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isChunkError = isChunkLoadError(error);
    return { 
      hasError: true, 
      error,
      isChunkLoadError: isChunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunkError = isChunkLoadError(error);
    
    this.setState({ 
      errorInfo,
      isChunkLoadError: isChunkError,
    });
    
    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    console.error('Is chunk load error:', isChunkError);
    
    // Report to backend error monitoring
    this.reportError(error, errorInfo, isChunkError);
  }

  reportError = (error: Error, errorInfo: ErrorInfo, isChunkError: boolean = false) => {
    // Store in localStorage for local debugging
    const errorReport = {
      timestamp: new Date().toISOString(),
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      isChunkError,
    };
    
    try {
      const existingErrors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      existingErrors.push(errorReport);
      // Keep only last 10 errors locally
      if (existingErrors.length > 10) existingErrors.shift();
      localStorage.setItem('app_errors', JSON.stringify(existingErrors));
    } catch (e) {
      console.error('Failed to store error report locally:', e);
    }

    // Send to backend for production monitoring
    reportErrorToBackend(error, errorInfo, isChunkError);
  };

  handleReload = () => {
    // Force a hard reload to clear any cached state
    window.location.reload();
  };

  handleGoHome = () => {
    // Use full URL to ensure proper navigation
    window.location.href = window.location.origin + '/dashboard';
  };

  handleRetry = async () => {
    const { isChunkLoadError, retryCount } = this.state;
    const MAX_RETRIES = 3;

    // For chunk load errors, clear cache before retry
    if (isChunkLoadError && retryCount < MAX_RETRIES) {
      try {
        // Clear service worker cache if available
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }
        
        // Clear browser cache by adding timestamp to force reload
        const newRetryCount = retryCount + 1;
        this.setState({ 
          hasError: false, 
          error: null, 
          errorInfo: null,
          retryCount: newRetryCount,
        });
        
        // Small delay before retry to allow cache clearing
        setTimeout(() => {
          window.location.reload();
        }, 100);
        return;
      } catch (e) {
        console.error('Failed to clear cache:', e);
      }
    }

    // For non-chunk errors or after max retries, just reset state
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorDisplay 
          error={this.state.error}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
          onRetry={this.handleRetry}
          isChunkLoadError={this.state.isChunkLoadError}
          retryCount={this.state.retryCount}
        />
      );
    }

    return this.props.children;
  }
}

// Fallback translations for when LanguageContext fails
const fallbackTranslations: Record<string, string> = {
  'errorBoundary.title': 'Something went wrong',
  'errorBoundary.description': 'An unexpected error occurred. Please try again.',
  'errorBoundary.tryAgain': 'Try Again',
  'errorBoundary.goHome': 'Go Home',
  'errorBoundary.helpText': 'If the problem persists, please contact support.',
  'errorBoundary.errorDetails': 'Error Details',
  'errorBoundary.chunkLoadError.title': 'Failed to Load Page',
  'errorBoundary.chunkLoadError.description': 'The page could not be loaded. This may be due to a network issue or outdated cache.',
  'errorBoundary.chunkLoadError.retry': 'Try Again',
  'errorBoundary.chunkLoadError.retryMessage': 'Retrying...',
};

// Functional component for error display with translations
interface ErrorDisplayProps {
  error: Error | null;
  onReload: () => void;
  onGoHome: () => void;
  onRetry: () => void;
  isChunkLoadError?: boolean;
  retryCount?: number;
}

function ErrorDisplay({ error, onReload, onGoHome, onRetry, isChunkLoadError = false, retryCount = 0 }: ErrorDisplayProps) {
  // Wrap useTranslation in try-catch to prevent cascade failures
  let t: (key: string, fallback?: string) => string;
  try {
    const translation = useTranslation();
    t = translation.t;
  } catch {
    // Fallback if translation context fails
    t = (key: string, fallback?: string) => fallbackTranslations[key] || fallback || key;
  }
  
  const MAX_RETRIES = 3;
  const canRetry = isChunkLoadError && retryCount < MAX_RETRIES;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-destructive/20 bg-card/80 backdrop-blur-sm">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            {isChunkLoadError 
              ? t('errorBoundary.chunkLoadError.title', 'Failed to Load Page')
              : t('errorBoundary.title')
            }
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {isChunkLoadError
              ? t('errorBoundary.chunkLoadError.description', 'The page could not be loaded. This may be due to a network issue or outdated cache.')
              : t('errorBoundary.description')
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {process.env.NODE_ENV === 'development' && error && (
            <div className="rounded-lg bg-muted/50 p-4 text-sm">
              <div className="flex items-center gap-2 mb-2 text-destructive font-medium">
                <Bug className="h-4 w-4" />
                {t('errorBoundary.errorDetails')}
              </div>
              <code className="text-xs text-muted-foreground block overflow-auto max-h-32">
                {error.message}
              </code>
            </div>
          )}
          
          {canRetry ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground text-center">
                {t('errorBoundary.chunkLoadError.retryMessage', `Attempt ${retryCount + 1} of ${MAX_RETRIES}`)}
              </p>
              <Button 
                onClick={onRetry} 
                variant="default"
                className="w-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('errorBoundary.chunkLoadError.retry', 'Try Again')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={onReload} 
                variant="default"
                className="flex-1"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('errorBoundary.tryAgain')}
              </Button>
              <Button 
                onClick={onGoHome} 
                variant="outline"
                className="flex-1"
              >
                <Home className="mr-2 h-4 w-4" />
                {t('errorBoundary.goHome')}
              </Button>
            </div>
          )}
          
          <p className="text-center text-xs text-muted-foreground pt-2">
            {t('errorBoundary.helpText')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Hook-friendly wrapper for functional components
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}
